<template>
  <div class="supplement content-wrapper">
    <div class="page-header">{{ $t('newSupplementCharge') }}</div>
    <div style="margin-bottom: 40px; margin-left: 5px;">
      <el-breadcrumb separator="/">
        <el-breadcrumb-item :to="{ name: 'HotelSupplementItemList' }">{{ $t('supplementChargeList') }}</el-breadcrumb-item>
        <el-breadcrumb-item>{{ $t('newSupplementCharge') }}</el-breadcrumb-item>
      </el-breadcrumb>
    </div>
    <el-form ref="form" label-width="150px" style="width: 500px">
      <el-form-item :label="$t('name')" :required="true">
        <el-input v-model="supplementCharge.name"></el-input>
      </el-form-item>
      <el-form-item :label="$t('type')" :required="true">
        <el-select v-model="supplementCharge.supplementTypeId" :placeholder="$t('pleaseSelectType')" style="width: 100%">
          <el-option :label="supplementType.name" :value="supplementType.id" v-for="supplementType in supplementTypeList" :key="supplementType.id"></el-option>
          <el-option :label="$t('otherType')" :value="SUPPLEMENT_TYPE.OTHER"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item label=" " v-if="supplementCharge.supplementTypeId === SUPPLEMENT_TYPE.OTHER" :required="true">
        <el-input v-model="supplementCharge.supplementTypeName"></el-input>
      </el-form-item>
      <el-form-item :label="$t('rate')">
        <el-row>
          <el-col :span="12">
            <el-input v-model="supplementCharge.price" type="number"></el-input>
          </el-col>
          <el-col :span="12" style="padding-left: 5px;">
            <el-select v-model="supplementCharge.priceType">
              <el-option :label="$t(`${supplementPriceType.titleKey}`)" :value="supplementPriceType.id" v-for="supplementPriceType in supplementPriceTypeList" :key="supplementPriceType.id"></el-option>
            </el-select>
          </el-col>
        </el-row>
      </el-form-item>
      <el-form-item :label="$t('unit')">
        <el-select v-model="supplementCharge.unitType" style="width: 100%">
          <el-option :label="$t(`${supplementUnitType.titleKey}`)" :value="supplementUnitType.id" v-for="supplementUnitType in supplementUnitTypeList" :key="supplementUnitType.id"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item :label="$t('periodOfStay')">
        <el-row>
          <el-checkbox v-model="allTheTime">{{ $t('allTheTime') }}</el-checkbox>
        </el-row>
        <el-row v-for="(period, index) in periodList" :key="index">
          <label v-if="index > 0" class="label-remove-period">
            <i class="el-icon-remove" style="color: red" @click="onRemovePeriodButtonClick(index)"></i>
          </label>
          <el-col :span="24">
            <el-date-picker size="small" :disabled="Boolean(allTheTime)"
                            v-model="periodList[index]"
                            type="daterange"
                            format="d MMM yyyy"
                            value-format="yyyy-MM-dd"
                            start-placeholder="From"
                            end-placeholder="To"
                            @change="onDatePickerChange(index)">
            </el-date-picker>
          </el-col>
        </el-row>
        <el-row>
          <el-col :span="24">
            <el-button  plain type="primary" icon="el-icon-plus" @click="onAddPeriodButtonClick()" size="mini" style="width: 100%" :disabled="Boolean(allTheTime)">
              {{ $t('addPeriod') }}
            </el-button>
          </el-col>
        </el-row>
      </el-form-item>
      <el-form-item :label="$t('conditionDetail')">
        <el-input type="textarea" :rows="3" v-model="supplementCharge.conditionDetail"></el-input>
      </el-form-item>
      <el-form-item label="">
        <el-button type="primary" @click="onCreateSupplementButtonClick">{{ $t('createSupplementCharge') }}</el-button>
        <el-button type="default" @click="onCancelButtonClick">{{ $t('cancel') }}</el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
import { STATUS, SUPPLEMENT_UNIT_TYPE, SUPPLEMENT_PRICE_TYPE, SUPPLEMENT_TYPE } from '@/configs/constant'
import i18nMessages from '@/translations/Hotel/ManageHotel/Supplement/ManageSupplement/Item/Add.i18n'
import AlertViewMixin from '@/mixins/alert-view-mixin'
import LoadingViewMixin from '@/mixins/loading-view-mixin'
import NotificationMixin from '@/mixins/notification-mixin'
import { supplementUnitTypeList, supplementPriceTypeList, getHotelIndexRoute } from '@/services/hotel-service'
import HotelSupplementApi from '@/api/hotel/supplement-api'
import LocalStorageFactory from '@/factories/local-storage-factory'
import { periodOverlap, preparePeriodList, getValidPeriodList } from '@/helpers/index'
import { mapState } from 'vuex'

export default {
  name: 'HotelSupplementItemAdd',
  mixins: [AlertViewMixin, LoadingViewMixin, NotificationMixin],
  components: { },
  i18n: { messages: i18nMessages },
  props: {
    supplementTypeList: {
      type: Array,
      default: function () {
        return []
      }
    }
  },
  created: async function () {
    if (this.activeHotelId === undefined || this.activeHotelId === '') {
      const companyDetail = LocalStorageFactory.getCompanyDetail()
      const routeName = await getHotelIndexRoute(companyDetail, this.$store)

      return this.$router.push({
        name: routeName
      })
    }
  },
  computed: {
    ...mapState({
      activeHotelId: state => state.general.activeHotelId
    })
  },
  data () {
    return {
      STATUS,
      SUPPLEMENT_TYPE,
      supplementUnitTypeList,
      supplementPriceTypeList,
      allTheTime: false,
      periodList: [''],
      supplementCharge: {
        name: '',
        supplementTypeId: '',
        supplementTypeName: '',
        price: 0,
        priceType: SUPPLEMENT_PRICE_TYPE.NET,
        unitType: SUPPLEMENT_UNIT_TYPE.PER_PERSON,
        conditionDetail: '',
        periodOfStayList: []
      }
    }
  },
  methods: {
    onCancelButtonClick: function () {
      this.$router.push({
        name: 'HotelSupplementItemList'
      })
    },
    onCreateSupplementButtonClick: async function () {
      if (!this._validatePeriodList()) {
        return false
      }

      try {
        const periodList = preparePeriodList(this.periodList)
        const periodOfStayList = {
          periodOfStayList: periodList
        }

        const data = Object.assign({}, this.supplementCharge, periodOfStayList)
        await HotelSupplementApi.create(this.activeHotelId, data)
        this.$router.push({
          name: 'HotelSupplementItemList'
        })

        const title = this.$t('createSupplementChargeSuccessTitle')
        const message = this.$t('createSupplementChargeSuccessMessage')

        this.showNotificationSuccess(title, message)
      } catch (err) {
        this.alertError(err.message)
      }

      this.stopLoading()
    },
    onRemovePeriodButtonClick: function (index) {
      this.periodList.splice(index, 1)
    },
    onAddPeriodButtonClick: function () {
      this.periodList.push('')
    },
    onDatePickerChange: function (index) {
      const periodList = getValidPeriodList(this.periodList)
      const newPeriodList = preparePeriodList(periodList)

      if (periodOverlap(newPeriodList) === true) {
        const message = this.$t('alertOverlap')
        this.alertError(message)

        this.periodList[index][0] = ''
        this.periodList[index][1] = ''
      }
    },
    _validatePeriodList: function () {
      if (this.allTheTime === true) {
        return true
      }

      const periodList = getValidPeriodList(this.periodList)

      if (periodList.length < 1) {
        let message = this.$t('pleaseSelectPeriodOfStay')
        this.alertError(message)

        return false
      }

      this.periodList = [ ...periodList ]

      return true
    }
  }
}
</script>

<style scoped>
  #room-type-list-table {
    width: 100%;
    border: 1px solid #dddddd;
    border-radius: 5px;
    box-shadow: 0px 3px 5px 1px #eeeeee;
  }
  .el-table th {
    background-color: #eeeeee;
  }
  .el-form > div {
    text-align: left;
  }
  .label-remove-period {
    position: absolute;
    left: -25px;
    cursor: pointer
  }
</style>
